import { Card, Tag } from 'antd';
import { ReactNode, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import apiCallFlex from 'apps/robo-advisory/apiCalls/portfolioFlex';
import apiCallFund from 'apps/robo-advisory/apiCalls/portfolioFund';
import apiCallStock from 'apps/robo-advisory/apiCalls/portfolioStock';
import apiUserAssestsDetailCall from 'apps/investment/apiCalls/security-user/assets-detail';
import apiSecurityUserStocksCall from 'apps/investment/apiCalls/security-user/stocks';
import apiStockTransferCall from 'apps/robo-advisory/apiCalls/stock-transfer';
import portfolioFundGoalsApiCall from 'apps/investment/apiCalls/user/portfolioFundGoals';
import portfolioStockGoalsApiCall from 'apps/investment/apiCalls/user/portfolioStockGoals';
import userProgramApiCall from 'apps/investment/apiCalls/userProgram';
import { companyColors } from 'apps/investment/constants';
import { DataTable } from 'common/data-table';
import CashSVG from 'common/svg/cash-svg';
import { LabelTooltip } from 'common/ui/label-tooltip';
import Loading from 'common/ui/loading';
import { useFetchApiGet } from 'hooks/api';

import { formatCurrency, formatNumber } from 'utils/formatAmount';
import { formatDateTime } from 'utils/formatDate';
import {
  goalColumns,
  portfolioFlexColumns,
  portfolioFundColumns,
  portfolioFundGoalColumns,
  portfolioStockColumns,
  portfolioStockGoalColumns,
  securityUserStocksColumns,
  stockTransferColumns,
} from './config';

const ItemDetail = ({ item }) => {
  const userId = item.user_id;

  const {
    data: portfolioFunds = [],
    load: fetchPortfolioFunds,
    isLoading: isLoadingPortfolioFunds,
    error: portfolioFundsError,
  } = useFetchApiGet(apiCallFund.list, { resourceName: 'data' });

  const {
    data: portfolioStocks = [],
    load: fetchPortfolioStocks,
    isLoading: isLoadingPortfolioStocks,
    error: portfolioStocksError,
  } = useFetchApiGet(apiCallStock.list, { resourceName: 'data' });

  const {
    data: portfolioFlexs = [],
    load: fetchPortfolioFlexs,
    isLoading: isLoadingPortfolioFlexs,
    error: portfolioFlexsError,
  } = useFetchApiGet(apiCallFlex.list, { resourceName: 'data' });

  // NHSV data - load independently without blocking the page or showing errors
  const { data: userAssets, isLoading: isLoadingUserAssets, load: loadUserAssets } = useFetchApiGet(
    apiUserAssestsDetailCall,
    { resourceName: 'data' }
  );

  const { data: securityUserStocksData, isLoading: isLoadingSecurityUserStocks, load: loadSecurityUserStocks } = useFetchApiGet(
    apiSecurityUserStocksCall,
    { resourceName: 'data' }
  );
  const securityUserStocks = securityUserStocksData || [];

  // Stock transfers data
  const { data: stockTransfersData, isLoading: isLoadingStockTransfers, load: loadStockTransfers } = useFetchApiGet(
    apiStockTransferCall.list,
    { resourceName: 'data' }
  );
  const stockTransfers = stockTransfersData || [];

  // Goals (user programs)
  const { data: goalsData, isLoading: isLoadingGoals, load: loadGoals } = useFetchApiGet(
    userProgramApiCall.list,
    { resourceName: 'data' }
  );
  const goals = goalsData || [];

  // Portfolio fund goals
  const { data: fundGoalItemsData, isLoading: isLoadingFundGoals, load: loadFundGoals } = useFetchApiGet(
    portfolioFundGoalsApiCall,
    { resourceName: 'data' }
  );
  const fundGoalItems = fundGoalItemsData || [];

  // Portfolio stock goals
  const { data: stockGoalItemsData, isLoading: isLoadingStockGoals, load: loadStockGoals } = useFetchApiGet(
    portfolioStockGoalsApiCall,
    { resourceName: 'data' }
  );
  const stockGoalItems = stockGoalItemsData || [];

  useEffect(() => {
    if (userId) {
      fetchPortfolioFunds({ user_id: userId });
      fetchPortfolioStocks({ user_id: userId });
      fetchPortfolioFlexs({ userId: userId });
      loadUserAssets({ id: userId });
      loadSecurityUserStocks({ user_id: userId });
      loadStockTransfers({ user_id: userId, page_size: 20 });
      loadGoals({ user_id: userId });
      loadFundGoals({ id: userId });
      loadStockGoals({ id: userId });
    }
  }, []);

  // Only block the page for core portfolio data, not NHSV data
  const isLoading =
    isLoadingPortfolioFunds ||
    isLoadingPortfolioStocks ||
    isLoadingPortfolioFlexs;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[80vh]">
        <Loading />
      </div>
    );
  }

  const isPositive = item?.unrealized_gain_loss_amount >= 0;
  const trendColor = isPositive ? 'text-green-500' : 'text-red-500';

  return (
    <>
      {/* User Info Section */}
      <Card className="my-3 shadow-sm border-l-4 border-l-blue-500" bodyStyle={{ padding: '12px 16px' }}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Link
              to={`/investment/users/${item.user_id}`}
              className={`flex h-9 w-9 items-center justify-center rounded-full flex-shrink-0 transition-transform hover:scale-110 ${
                item?.user?.account_type === 'business'
                  ? 'bg-blue-100 hover:bg-blue-200'
                  : 'bg-green-100 hover:bg-green-200'
              }`}
            >
              {item?.user?.account_type === 'business' ? (
                <svg
                  className="h-5 w-5 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              ) : (
                <svg
                  className="h-5 w-5 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              )}
            </Link>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Link
                  to={`/investment/users/${item.user_id}`}
                  className="font-semibold text-gray-900 hover:text-blue-600 transition-colors text-sm sm:text-base"
                >
                  {item?.user?.name || 'N/A'}
                </Link>
                <Tag
                  color={item?.user?.account_type === 'business' ? 'blue' : 'green'}
                  className="m-0 text-xs"
                >
                  {item?.user?.account_type === 'business' ? 'Business' : 'Individual'}
                </Tag>
              </div>
              <div className="text-xs text-gray-600 truncate mt-0.5">{item?.user?.email || 'N/A'}</div>
            </div>
          </div>
          <Link
            to={`/investment/users/${item.user_id}`}
            className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors flex-shrink-0 sm:self-auto self-start ml-12 sm:ml-0"
          >
            <span>Chi tiết</span>
            <svg
              className="h-3 w-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </Link>
        </div>
      </Card>

      {/* Portfolio Overview */}
      <Card className="my-4 shadow-md">
        <div className="flex items-center justify-between mb-4">
          <div className="text-base font-semibold text-gray-700">Tổng quan danh mục</div>
          <div className="flex items-center gap-3">
            <Link
              to={`/robo-advisory/portfolios/${item.user_id}/analysis`}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Phân tích danh mục →
            </Link>
            <div className="text-sm text-gray-500">
              Cập nhật: {formatDateTime(item?.updated_at)}
            </div>
          </div>
        </div>

        {/* Block 1: Tổng quan */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          <StatCard
            title="Giá trị danh mục"
            value={formatCurrency(item?.market_value)}
            valueClass={trendColor}
          />
          <StatCard
            title="Lãi/Lỗ"
            value={
              <span>
                {formatCurrency(item?.unrealized_gain_loss_amount)}
                <span className="text-xs">
                  ({formatNumber(item?.unrealized_gain_loss_percentage)}%)
                </span>
              </span>
            }
            valueClass={trendColor}
          />
          <StatCard
            title="Vốn mua"
            value={formatCurrency(item?.buy_value)}
            valueClass="text-blue-600"
          />
        </div>

        {/* Block 2: Giao dịch */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            title="Tổng đã mua"
            value={formatCurrency(item?.total_buy_amount)}
          />
          <StatCard
            title="Tổng đã bán"
            value={formatCurrency(item?.total_sell_amount)}
          />
          <StatCard
            title="Mua chưa khớp"
            value={formatCurrency(item?.total_pending_buy_amount)}
          />
          <StatCard
            title="Bán chưa khớp"
            value={formatCurrency(item?.total_pending_sell_amount)}
          />
        </div>
      </Card>

      {portfolioStocks && portfolioStocks.length > 0 && !portfolioStocksError && (
        <Card className="my-4 shadow-md" title="Danh mục chứng khoán">
          <DataTable
            columns={portfolioStockColumns}
            data={portfolioStocks}
            tableName="userPortfolioStocks"
            noViewOptions={true}
          />
        </Card>
      )}

      {portfolioFunds && portfolioFunds.length > 0 && !portfolioFundsError && (
        <Card className="my-4 shadow-md" title="Danh mục chứng chỉ quỹ">
          <DataTable
            columns={portfolioFundColumns}
            data={portfolioFunds}
            tableName="userPortfolioFund"
            noViewOptions={true}
          />
        </Card>
      )}
      {portfolioFlexs && portfolioFlexs.length > 0 && !portfolioFlexsError && (
        <Card className="my-4 shadow-md" title="Danh mục Future Flex">
          <DataTable
            columns={portfolioFlexColumns}
            data={portfolioFlexs}
            tableName="userPortfolioFlexs"
            noViewOptions={true}
          />
        </Card>
      )}

      {/* Stock Transfers Section */}
      {stockTransfers && stockTransfers.length > 0 && (
        <Card
          className="my-4 shadow-md"
          title={
            <div className="flex items-center justify-between w-full">
              <span>Lịch sử chuyển chứng khoán</span>
              {stockTransfers.length >= 20 && (
                <Link
                  to={`/investment/stock-transfers?user_id=${userId}`}
                  className="text-sm text-blue-600 hover:text-blue-700 font-normal"
                >
                  Xem tất cả →
                </Link>
              )}
            </div>
          }
        >
          <DataTable
            columns={stockTransferColumns}
            data={stockTransfers}
            tableName="userStockTransfers"
            noViewOptions={true}
          />
        </Card>
      )}

      {(isLoadingGoals || goals.length > 0) && (
        <Card className="my-4 shadow-md" title="Danh sách mục tiêu">
          <DataTable
            columns={goalColumns}
            data={goals}
            tableName="portfolioGoals"
            noViewOptions={true}
          />
        </Card>
      )}

      {(isLoadingFundGoals || fundGoalItems.length > 0) && (
        <Card className="my-4 shadow-md" title="Danh mục theo mục tiêu quỹ">
          <DataTable
            columns={portfolioFundGoalColumns}
            data={fundGoalItems}
            tableName="portfolioFundGoals"
            noViewOptions={true}
          />
        </Card>
      )}

      {(isLoadingStockGoals || stockGoalItems.length > 0) && (
        <Card className="my-4 shadow-md" title="Danh mục theo mục tiêu chứng khoán">
          <DataTable
            columns={portfolioStockGoalColumns}
            data={stockGoalItems}
            tableName="portfolioStockGoals"
            noViewOptions={true}
          />
        </Card>
      )}

      {(isLoadingUserAssets || isLoadingSecurityUserStocks || userAssets || securityUserStocks?.length > 0) && (
        <Card
          title={
            <span className="text-xl text-white">Dữ liệu tài sản (NHSV)</span>
          }
          className="my-4 shadow-md border-nhsv overflow-hidden"
          styles={{
            header: {
              backgroundColor: companyColors['nhsv'],
              padding: '16px 24px',
            },
            body: { padding: '24px' },
          }}
        >
          {isLoadingUserAssets || isLoadingSecurityUserStocks ? (
            <div className="flex justify-center items-center py-8">
              <Loading />
            </div>
          ) : (
            <>
              {userAssets && <AssetsDetail item={userAssets} />}
              {securityUserStocks && securityUserStocks.length > 0 && (
                <div className="pt-6">
                  <h3 className="text-base font-medium text-gray-800 mb-4">
                    Số dư chứng khoán
                  </h3>
                  <DataTable
                    columns={securityUserStocksColumns}
                    data={securityUserStocks}
                    tableName="securityUserStocks"
                    noViewOptions={true}
                  />
                </div>
              )}
              {!userAssets && (!securityUserStocks || securityUserStocks.length === 0) && (
                <div className="text-center text-gray-500 py-8">
                  Tài khoản này không có dữ liệu NHSV
                </div>
              )}
            </>
          )}
        </Card>
      )}
    </>
  );
};

export default ItemDetail;

const StatCard = ({
  title,
  value,
  valueClass = '',
  to,
}: {
  title?: ReactNode;
  value?: ReactNode;
  valueClass?: string;
  to?: string;
}) => {
  const navigate = useNavigate();
  const isClickable = !!to;

  return (
    <div
      className={`flex flex-col items-center px-1 py-1 border border-gray-200 rounded-md transition
        ${
          isClickable
            ? 'cursor-pointer hover:shadow hover:border-blue-400 bg-blue-50'
            : ''
        }
      `}
      onClick={isClickable ? () => navigate(to) : undefined}
      tabIndex={isClickable ? 0 : undefined}
      role={isClickable ? 'button' : undefined}
      aria-label={isClickable ? `Xem chi tiết ${title}` : undefined}
    >
      <span className={`font-bold ${valueClass} text-base`}>{value}</span>
      <span className="text-[11px] text-gray-400 mt-0.5 flex items-center gap-1">
        {title}
        {isClickable && (
          <svg
            width="12"
            height="12"
            fill="none"
            viewBox="0 0 24 24"
            className="inline text-blue-400"
          >
            <path
              d="M9 18l6-6-6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
    </div>
  );
};

const AssetsDetail = ({ item }) => {
  return (
    <>
      <div>
        <div className="text-xs text-gray-500">Net Asset Value</div>
        <div className="text-2xl font-bold text-blue-700">
          {formatNumber(item.net_asset_value)}
        </div>
      </div>
      {/* Cash Section */}
      <div className="mb-8">
        <h3 className="text-base font-medium text-gray-800 mb-4 pb-2 border-b flex items-center">
          <CashSVG className="mr-2" />
          Tiền mặt
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatCard
            title={
              <LabelTooltip
                label="Tiền khả dụng"
                tooltipContent="available_cash = cash_not_hold (Tiền chưa phong tỏa)"
              />
            }
            value={formatNumber(item.available_cash)}
            valueClass="text-green-600"
          />
          <StatCard
            title="Tổng tiền mặt"
            value={formatNumber(item.total_cash)}
            valueClass="text-blue-600"
          />
          <StatCard
            title="Tiền bị phong tỏa"
            value={formatNumber(item.blocked_cash)}
          />
          <StatCard
            title="Tiền mua chưa khớp"
            value={formatNumber(item.pending_purchase_amount)}
          />
          <StatCard
            title="Tiền mua khớp chưa thanh toán"
            value={formatNumber(item.unpaid_purchase_amount)}
          />
          <StatCard
            title="Tiền cổ tức chờ về"
            value={formatNumber(item.incoming_dividend_cash)}
          />
        </div>
      </div>
      {/* Stock Section */}
      <div className="mb-8">
        <h3 className="text-base font-medium text-gray-800 mb-4 pb-2 border-b flex items-center">
          <svg
            className="w-5 h-5 mr-2 text-green-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          Giá trị chứng khoán và chứng chỉ quỹ
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Thị trường"
            value={formatNumber(item.total_stock_market_value)}
          />
          <StatCard
            title="Được phép giao dịch"
            value={formatNumber(item.available_stock_value)}
          />
          <StatCard
            title="Mua chờ về"
            value={formatNumber(item.incoming_stock_value)}
          />
          <StatCard
            title="Bị phong tỏa"
            value={formatNumber(item.blocked_stock_value)}
          />
        </div>
      </div>
      {/* Debt Section */}
      <div className="mb-8">
        <h3 className="text-base font-medium text-gray-800 mb-4 pb-2 border-b flex items-center">
          <svg
            className="w-5 h-5 mr-2 text-amber-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Thông tin nợ
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard title="Tổng nợ" value={formatNumber(item.total_debt)} />
          <StatCard title="Thực nợ" value={formatNumber(item.real_debt)} />
          <StatCard
            title="Tiền bán chưa ứng"
            value={formatNumber(item.cash_advance)}
          />
          <StatCard
            title="Nợ phí lưu ký"
            value={formatNumber(item.depository_fee)}
          />
          <StatCard
            title="Lãi vay tạm tính"
            value={formatNumber(item.estimated_loan_interest)}
          />
        </div>
      </div>
      {/* Timestamps */}
      <div className="bg-gray-50 -mx-6 -mb-6 p-6 rounded-b-lg mt-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-3 rounded shadow-sm">
            <div className="text-xs font-medium text-gray-500">Asset Date</div>
            <div className="mt-1 font-medium">
              {formatDateTime(item.asset_date)}
            </div>
          </div>
          <div className="bg-white p-3 rounded shadow-sm">
            <div className="text-xs font-medium text-gray-500">Synced At</div>
            <div className="mt-1 font-medium">
              {formatDateTime(item.synced_at)}
            </div>
          </div>
          <div className="bg-white p-3 rounded shadow-sm">
            <div className="text-xs font-medium text-gray-500">Created At</div>
            <div className="mt-1 font-medium">
              {formatDateTime(item.created_at)}
            </div>
          </div>
          <div className="bg-white p-3 rounded shadow-sm">
            <div className="text-xs font-medium text-gray-500">Updated At</div>
            <div className="mt-1 font-medium">
              {formatDateTime(item.updated_at)}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
